/**
 * T3C SDK 事件监听器注册工具
 * @description 统一管理 T3C SDK 的事件监听器注册逻辑，避免重复代码
 */

import { message } from 'antd'
import { t3cStore } from './t3c-store'
import { sessionStore } from './session-store'
import { moduleId } from '@configs'
import { LogType } from '@tencent/aegis-web-sdk-v2'
import { getT3CCallInRecordCallerBySessionId, submitRealtimeAsr } from '@services/t3c'
import { ASRItem } from 'src/types/ai-assistant'

/** ASR flow -> 角色文本（OUT 为坐席，其余为客户） */
const asrRole = (flow: string) => (flow === 'OUT' ? '坐席' : '客户')

/**
 * 挂断后提交实时 ASR 文本（趁 store 尚未 reset，先取快照再异步提交）
 * 失败不阻断通话结束流程；后端幂等，重试安全
 */
const submitRealtimeAsrOnHangup = (eventSessionId?: string, eventDuration?: number) => {
  const { asrList, sessionId, callInfo } = t3cStore.getState()
  console.log('[SubmitRealtimeAsr] 挂断提交检查', {
    asrLen: asrList.length,
    recordId: callInfo.recordId,
    sessionId,
    eventSessionId,
    eventDuration,
  })
  // 无 ASR 文本 / 缺少必要标识时不提交
  if (!asrList.length || !callInfo.recordId || !sessionId) {
    console.warn('[SubmitRealtimeAsr] 跳过提交：缺少必要数据', {
      hasAsr: !!asrList.length,
      hasRecordId: !!callInfo.recordId,
      hasSessionId: !!sessionId,
    })
    return
  }
  // 多标签场景防御：SDK 共享实例，事件会广播到所有标签页。
  // 事件会话与本标签当前会话不一致时不提交，避免串号提交到错误的通话记录
  if (eventSessionId && eventSessionId !== sessionId) {
    console.warn('[SubmitRealtimeAsr] 跳过提交：会话不匹配', { eventSessionId, sessionId })
    return
  }

  // 取快照，避免异步提交时被 resetCallState 清空
  const snapshot: ASRItem[] = asrList.slice()
  const recordId = callInfo.recordId
  const sid = sessionId
  // 本次通话时长（秒）：直接取 SDK sessionEnded 事件返回的 duration，未接通/取不到为 0
  const duration = typeof eventDuration === 'number' ? eventDuration : 0

  const content = snapshot.map(item => `${asrRole(item.flow)}：${item.message}`).join('\n')
  const conversation = JSON.stringify(snapshot.map(item => ({
    role: asrRole(item.flow),
    content: item.message,
    timestamp: item.timestamp,
    index: item.index,
    sliceType: item.sliceType,
  })))

  const trySubmit = async (retryLeft = 1) => {
    console.log('[SubmitRealtimeAsr] 发起提交', { recordId, sessionId: sid, duration, contentLen: content.length })
    const [rsp, err] = await submitRealtimeAsr({ recordId, sessionId: sid, content, conversation, duration })
    if (err) {
      if (retryLeft > 0) {
        // 文档建议失败可重试一次；后端幂等，重复提交返回 status=1
        await trySubmit(retryLeft - 1)
      } else {
        console.error('[SubmitRealtimeAsr] 提交失败', err)
      }
    } else {
      console.log('[SubmitRealtimeAsr] 提交成功', rsp)
    }
  }
  // 异步提交，捕获底层异常，避免产生未处理的 Promise rejection
  trySubmit().catch(e => console.error('[SubmitRealtimeAsr] 提交异常', e))
}

/**
 * 注册 T3C SDK 的基础事件监听器
 * 包括 ready、tokenExpired、callOuted、calloutAccepted、muted、asr、kickedOut 等
 */
export const registerT3cBaseListeners = () => {
  const {
    setCurTabUUID,
    setT3cReady,
    setT3cFailed,
    setT3cErrorMsg,
    setCallStatus,
    setSessionId,
    setCallInfo,
    setShowCallWorkbench,
    setIsCallMute,
    handleAsrEvent,
    setT3cInitStage,
  } = t3cStore.getState()

  // ready 事件
  window.tccc.on(window.tccc.events.ready, ({ tabUUID }) => {
    setCurTabUUID(tabUUID)
    setT3cReady(true)
    setT3cFailed(false)
    setT3cErrorMsg('')
    setT3cInitStage('ready')
    console.log('T3C SDK 初始化成功，当前客服状态：', window.tccc.Agent.getStatus(), 'tabUUID:', tabUUID)
  })

  // Token 过期事件
  window.tccc.on(window.tccc.events.tokenExpired, ({ message: errMsg, tabUUID }) => {
    console.error('初始化失败:', errMsg)
    const { curTabUUID } = t3cStore.getState()
    const { userInfo } = sessionStore.getState()
    message.error(`TCCC error: ${errMsg}。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}`)
    setT3cErrorMsg(errMsg)
    setT3cReady(true)
    setT3cFailed(true)
    console.log('T3C SDK 初始化失败. tabUUID:', tabUUID)
  })

  // 外呼成功事件
  window.tccc.on(window.tccc.events.callOuted, (res: any) => {
    setCallStatus('calling')
    console.log('外呼成功事件 window.tccc.events.callOuted', res)
    setSessionId(res.sessionId)
    setCallInfo((draft) => {
      draft.calleeLocation = res.calleeLocation
      draft.calleePhoneNumberText = res.calleePhoneNumberText
      draft.callerPhoneNumberText = res.callerPhoneNumberText
      draft.callTimestamp = 0
    })
    setShowCallWorkbench(true)
  })

  // 外呼对方接听事件
  window.tccc.on(window.tccc.events.calloutAccepted, (res: any) => {
    setCallStatus('talking')
    console.log('外呼对方接听事件 window.tccc.events.calloutAccepted', res)
    setSessionId(res.sessionId)
    setShowCallWorkbench(true)
    setCallInfo((draft) => {
      draft.callTimestamp = res.timestamp
    })
  })

  // 静音事件
  window.tccc.on('muted', () => {
    setIsCallMute(true)
  })

  // 语音识别事件
  window.tccc.on(window.tccc.events.asr, handleAsrEvent)

  // 踢出事件
  try {
    window.tccc.on(window.tccc.events.kickedOut, () => {
      const { curTabUUID } = t3cStore.getState()
      const { userInfo } = sessionStore.getState()
      message.error(`当前坐席在其他地方登录。tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}`)
      console.log('当前坐席在其他地方登录')
    })
  } catch (error) {
    console.log('T3C SDK kickedOut error:', error)
  }
}

/**
 * 注册 T3C SDK 的扩展事件监听器
 * 包括 userAccessed、sessionEnded 等需要在 t3cReady 后注册的事件
 * 这些事件依赖于用户权限、400热线配置等信息
 */
export const registerT3cExtendedListeners = () => {
  const { isPermissableBelongModule, userInfo } = sessionStore.getState()
  const { resetCallState, getTodayCallDuration } = t3cStore.getState()

  // 隐藏通知栏（SDR清洗模块且使用新通话条）
  if (isPermissableBelongModule([moduleId.SDR_CLEAN]) && userInfo.useNewT3CCallBar) {
    window.tccc.UI.hideNotificationBar()
  }

  // 标记来电是否已弹出表单（用于 sessionEnded 兜底）
  let lastCallInOpened = false
  let lastCallInRes: any = null

  // 来电显示事件
  const handleUserAccessed = async (res: any) => {
    // 实时获取最新的 hotline400Config 和 curTabUUID，避免闭包捕获过期值
    const { hotline400Config, curTabUUID } = t3cStore.getState()
    console.log('来电显示', res, 'hotline400Config:', hotline400Config)
    const result = hotline400Config?.list.find(item => item.hotline === res.calleePhoneNumber)
    window.AegisV2?.reportEvent({
      name: 'tccc_user_accessed',
      type: 'custom_event' as LogType,
      msg: `来电显示result: ${JSON.stringify(result)}, res.tabUUID: ${res.tabUUID}, curTabUUID: ${curTabUUID}, res.calleePhoneNumber, ${res.calleePhoneNumber}, res::, ${JSON.stringify(res)}`,
      eventId: Date.now(),
      user: userInfo?.salesName,
    })
    // 记录来电信息，供 sessionEnded 兜底使用
    lastCallInRes = res
    lastCallInOpened = false
    // 如果来源是乐享或电子签，跳转到通用400
    if (res.tabUUID === curTabUUID && result && result.redirect === 1) {
      if (result.from === 'meeting') {
        // 腾讯会议来源，需要通过sessionId获取实际的来电号码
        let callerPhone = res.callerPhoneNumber
        const [data, err] = await getT3CCallInRecordCallerBySessionId({ sessionId: res.sessionId })
        if (err) {
          message.error(`获取来电号码失败:${err?.message}`)
        } else {
          callerPhone = data?.caller
        }
        window.open(`/lead/hotline/call-success?qd_tel=${callerPhone}&qd_callid=t3c-${res.sessionId}&qd_kfext=${userInfo.email}&from=${result.from}`)
      } else {
        window.open(`/lead/hotline/clue-collect/400?qd_tel=${res.callerPhoneNumber}&qd_callid=t3c-${res.sessionId}&qd_kfext=${userInfo.email}&from=${result.from}`)
      }
      lastCallInOpened = true
    }
  }

  // 通话结束事件
  // 事件参数含 sessionId / closeBy / mainReason / subReason（详见 TCCC WebSDK 文档）
  const handleSessionEnded = (event?: { sessionId?: string; closeBy?: string; duration?: number }) => {
    console.log('通话结束', event)
    const eventSessionId = event?.sessionId
    // 注意：此处不再依据 sessionId 是否匹配来提前 return，
    // 以保证 resetCallState 等通话状态清理逻辑与原有流程完全一致（避免坐席通话条卡死）。
    // 多标签「不串号提交」的保护下沉到 submitRealtimeAsrOnHangup 内部的 sessionId 校验。

    // 兜底：如果来电已接入但表单未弹出（秒挂场景），在通话结束后补弹
    if (lastCallInRes && !lastCallInOpened) {
      const { hotline400Config, curTabUUID } = t3cStore.getState()
      const result = hotline400Config?.list.find(item => item.hotline === lastCallInRes.calleePhoneNumber)
      if (lastCallInRes.tabUUID === curTabUUID && result && result.redirect === 1) {
        if (result.from === 'meeting') {
          window.open(`/lead/hotline/call-success?qd_tel=${lastCallInRes.callerPhoneNumber}&qd_callid=t3c-${lastCallInRes.sessionId}&qd_kfext=${userInfo.email}&from=${result.from}`)
        } else {
          window.open(`/lead/hotline/clue-collect/400?qd_tel=${lastCallInRes.callerPhoneNumber}&qd_callid=t3c-${lastCallInRes.sessionId}&qd_kfext=${userInfo.email}&from=${result.from}`)
        }
      }
    }
    lastCallInRes = null
    lastCallInOpened = false
    // 提交实时 ASR 文本：必须在 resetCallState 之前触发（内部先取快照）
    // try-catch 兜底：任何异常都不得阻断后续 resetCallState，确保通话状态正常清理
    try {
      submitRealtimeAsrOnHangup(eventSessionId, event?.duration)
    } catch (e) {
      console.error('[SubmitRealtimeAsr] 提交前处理异常，已忽略以不影响通话结束流程', e)
    }
    resetCallState()
    // 延迟刷新通话时长
    setTimeout(() => {
      getTodayCallDuration()
    }, 2000)
    setTimeout(() => {
      getTodayCallDuration()
    }, 30000)
  }

  window.tccc.on(window.tccc.events.userAccessed, handleUserAccessed)
  window.tccc.on(window.tccc.events.sessionEnded, handleSessionEnded)
}
